import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslations } from "next-intl";
import { resolvePostLoginPath } from "@lms/backend/lib/auth/post-login-redirect";
import { OtpCodeForm } from "@/components/auth/otp-code-form";
import {
  fetchLoginRedirect,
  registerAccount,
  resendEmailOtp,
  verifyEmailOtp,
} from "@/services/api/auth";
import { apiErrorText } from "@/lib/api-error";
import { AUTH_ERROR_OPTIONS } from "@/lib/auth-error-options";
import {
  peekPendingInviteToken,
  rememberPendingInvite,
  takePendingInvitePath,
} from "@/lib/pending-invite";
import { fetchTenantBootstrap } from "@/services/api/tenants";
import { safeNextPath } from "@/lib/safe-next-path";
import { legalPath } from "@/lib/legal";
import { useAuthStore } from "@/stores/authStore";

export function TenantRegisterPage() {
  const { tenantSlug = "" } = useParams<{ tenantSlug: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const inviteToken = searchParams.get("invite");
  // PAY-9: a visitor sent here from a course (via sign-in) comes back to it after verifying.
  const nextPath = safeNextPath(searchParams.get("next"), tenantSlug);
  const nextQuery = nextPath ? `next=${encodeURIComponent(nextPath)}` : "";
  const t = useTranslations("auth");
  const tOtp = useTranslations("auth.otp");
  const tRoot = useTranslations();
  const setSession = useAuthStore((s) => s.setSession);
  const [challenge, setChallenge] = useState<{
    challengeToken: string;
    maskedEmail: string;
  } | null>(null);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [joinRequested, setJoinRequested] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  // §31: the same query (and cache) as the tenant layout — no extra request.
  const bootstrapQuery = useQuery({
    queryKey: ["tenant-bootstrap", tenantSlug],
    queryFn: () => fetchTenantBootstrap(tenantSlug),
    enabled: Boolean(tenantSlug),
  });
  const approvedOnly = bootstrapQuery.data?.tenant.joinPolicy === "approved_only";

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    // H4: the checkbox is required (the input is `required`; this covers scripted submits).
    if (!acceptedTerms) {
      setError(t("acceptTermsRequired"));
      return;
    }
    setLoading(true);
    setError(null);
    // Older invite links point here as ?invite=; carry it through verification and sign-in.
    if (inviteToken) rememberPendingInvite(tenantSlug, inviteToken);

    try {
      const staffInvite = inviteToken ?? peekPendingInviteToken(tenantSlug) ?? undefined;
      const result = await registerAccount({
        name,
        email,
        password,
        tenantSlug,
        acceptTerms: true,
        ...(staffInvite ? { inviteToken: staffInvite } : {}),
        ...(approvedOnly && message.trim() ? { message: message.trim() } : {}),
      });
      if (result.joinRequested) {
        // §31: not on the school's list — no account; the request went to the school admin.
        setJoinRequested(true);
      } else if (result.challengeToken) {
        setChallenge({
          challengeToken: result.challengeToken,
          maskedEmail: result.maskedEmail ?? email,
        });
      } else {
        // No code could be sent; signing in will send a fresh one (AUTH-008).
        navigate(`/t/${tenantSlug}/login?registered=1${nextQuery ? `&${nextQuery}` : ""}`);
      }
    } catch (err) {
      setError(apiErrorText(err, tRoot, "auth.registrationFailed", AUTH_ERROR_OPTIONS));
    } finally {
      setLoading(false);
    }
  }

  async function submitCode(code: string) {
    if (!challenge) return;
    try {
      const session = await verifyEmailOtp({ challengeToken: challenge.challengeToken, code });
      setSession(session.accessToken, session.user);
      const invitePath = takePendingInvitePath(tenantSlug);
      if (invitePath) {
        navigate(invitePath);
        return;
      }
      if (nextPath && session.user.tenantSlug === tenantSlug) {
        navigate(nextPath);
        return;
      }
      try {
        navigate((await fetchLoginRedirect()).redirectTo);
      } catch {
        navigate(resolvePostLoginPath(session.user.tenantSlug, session.user.role));
      }
    } catch (err) {
      throw new Error(apiErrorText(err, tRoot, "auth.otp.failed", { namespace: "auth.errors" }));
    }
  }

  async function resendCode() {
    if (!challenge) return;
    try {
      await resendEmailOtp(challenge.challengeToken);
    } catch (err) {
      throw new Error(apiErrorText(err, tRoot, "auth.otp.failed", { namespace: "auth.errors" }));
    }
  }

  if (joinRequested) {
    return (
      <main className="mx-auto flex min-h-screen max-w-md flex-col justify-center px-4">
        <div role="status" data-testid="register-join-requested">
          <h1 className="mb-2 text-2xl font-semibold">{t("joinRequestTitle")}</h1>
          <p className="text-sm">{t("joinRequestSent")}</p>
        </div>
        <p className="mt-6 text-sm">
          <Link
            to={`/t/${tenantSlug}/login${nextQuery ? `?${nextQuery}` : ""}`}
            className="text-[var(--text-heading)] underline"
          >
            {t("signInLink")}
          </Link>
        </p>
      </main>
    );
  }

  if (challenge) {
    return (
      <main className="mx-auto flex min-h-screen max-w-md flex-col justify-center px-4">
        <h1 className="mb-4 text-2xl font-semibold">{tOtp("verifyTitle")}</h1>
        <OtpCodeForm
          purpose="verify_email"
          maskedEmail={challenge.maskedEmail}
          onSubmit={submitCode}
          onResend={resendCode}
          onCancel={() => setChallenge(null)}
          cancelLabel={t("back")}
        />
      </main>
    );
  }

  return (
    <main className="mx-auto flex min-h-screen max-w-md flex-col justify-center px-4">
      <h1 className="mb-2 text-2xl font-semibold">{t("registerTitle")}</h1>
      <p className="mb-6 text-sm text-[var(--text-muted)]">
        {t("tenantLabel", { tenant: tenantSlug })}
      </p>
      {approvedOnly ? (
        <p className="mb-4 text-sm" data-testid="register-approved-only-hint">
          {t("approvedOnlyHint")}
        </p>
      ) : null}
      <form onSubmit={handleSubmit} className="space-y-4" data-testid="register-form">
        <div>
          <label htmlFor="name" className="mb-1 block text-sm font-medium">
            {t("name")}
          </label>
          <input
            id="name"
            data-testid="register-name"
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full rounded-md border border-[var(--border-strong)] px-3 py-2"
          />
        </div>
        <div>
          <label htmlFor="email" className="mb-1 block text-sm font-medium">
            {t("email")}
          </label>
          <input
            id="email"
            data-testid="register-email"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full rounded-md border border-[var(--border-strong)] px-3 py-2"
          />
        </div>
        <div>
          <label htmlFor="password" className="mb-1 block text-sm font-medium">
            {t("password")}
          </label>
          <input
            id="password"
            data-testid="register-password"
            type="password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full rounded-md border border-[var(--border-strong)] px-3 py-2"
          />
          <p className="mt-1 text-xs text-[var(--text-muted)]">{t("passwordHintLong")}</p>
        </div>
        {approvedOnly ? (
          <div>
            <label htmlFor="join-message" className="mb-1 block text-sm font-medium">
              {t("joinMessageLabel")}
            </label>
            <textarea
              id="join-message"
              data-testid="register-message"
              maxLength={500}
              rows={3}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              className="w-full rounded-md border border-[var(--border-strong)] px-3 py-2"
            />
            <p
              className="mt-1 text-xs text-[var(--text-muted)]"
              data-testid="register-join-privacy-note"
            >
              {t("joinRequestPrivacyNote")}
            </p>
          </div>
        ) : null}
        <label className="flex items-start gap-2 text-sm">
          <input
            type="checkbox"
            required
            className="mt-1"
            checked={acceptedTerms}
            onChange={(e) => setAcceptedTerms(e.target.checked)}
            data-testid="register-accept-terms"
          />
          <span>
            {t.rich("acceptTermsLabel", {
              terms: (chunks) => (
                <Link
                  to={legalPath(tenantSlug, "terms")}
                  target="_blank"
                  className="text-[var(--text-heading)] underline"
                  data-testid="register-terms-link"
                >
                  {chunks}
                </Link>
              ),
              privacy: (chunks) => (
                <Link
                  to={legalPath(tenantSlug, "privacy")}
                  target="_blank"
                  className="text-[var(--text-heading)] underline"
                  data-testid="register-privacy-link"
                >
                  {chunks}
                </Link>
              ),
            })}
          </span>
        </label>
        {error && (
          <p role="alert" className="text-sm text-[var(--danger)]" data-testid="register-error">
            {error}
          </p>
        )}
        <button
          type="submit"
          data-testid="register-submit"
          disabled={loading}
          className="w-full rounded-md bg-[var(--accent-strong)] px-4 py-2 text-[var(--accent-foreground)] hover:bg-[var(--accent-strong-hover)] disabled:opacity-50"
        >
          {loading ? t("creatingAccount") : t("createAccount")}
        </button>
      </form>
      <p className="mt-4 text-center text-sm">
        {t("haveAccount")}{" "}
        <Link
          to={`/t/${tenantSlug}/login${nextQuery ? `?${nextQuery}` : ""}`}
          className="text-[var(--text-heading)] underline"
        >
          {t("signInLink")}
        </Link>
      </p>
    </main>
  );
}
